import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Search, MapPin, Building2, Phone, Globe, ArrowRight, Newspaper, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import Seo from '../components/Seo';
import ArticleCard from '../components/ArticleCard';
import { Reveal, RevealGroup, RevealItem } from '../components/motion/Reveal';

const API = process.env.REACT_APP_BACKEND_URL;

const formatDate = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d)) return String(value).slice(0, 20);
  return d.toLocaleDateString('en-NG', { year: 'numeric', month: 'short', day: 'numeric' });
};

const PropertyHub = () => {
  const [articles, setArticles] = useState([]);
  const [developments, setDevelopments] = useState([]);
  const [meta, setMeta] = useState({ categories: [], locations: [] });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [filters, setFilters] = useState({ q: '', category: '', location: '', date: '' });
  const [searchInput, setSearchInput] = useState('');

  useEffect(() => {
    fetch(`${API}/api/hub/meta`).then((r) => r.json()).then(setMeta).catch(() => {});
    fetch(`${API}/api/hub/developments`).then((r) => r.json()).then((d) => setDevelopments(d.developments || [])).catch(() => {});
  }, []);

  const loadArticles = useCallback(async (f, p) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(p), limit: '9' });
      if (f.q) params.set('q', f.q);
      if (f.category) params.set('category', f.category);
      if (f.location) params.set('location', f.location);
      if (f.date) {
        params.set('from_date', f.date + 'T00:00:00Z');
        params.set('to_date', f.date + 'T23:59:59Z');
      }
      const res = await fetch(`${API}/api/hub/articles?${params}`);
      const data = await res.json();
      setArticles(data.articles || []);
      setPages(data.pages || 1);
    } catch (e) {
      setArticles([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadArticles(filters, page);
  }, [filters, page, loadArticles]);

  const applySearch = (e) => {
    e.preventDefault();
    setPage(1);
    setFilters((f) => ({ ...f, q: searchInput }));
  };

  const setFilter = (key, value) => {
    setPage(1);
    setFilters((f) => ({ ...f, [key]: f[key] === value ? '' : value }));
  };

  return (
    <div className="min-h-screen bg-white">
      <Seo
        title="Versatile Property Hub — Lagos Property & Land News"
        description="Property news, land updates, market insights and development alerts for Lagos and Nigeria — reviewed and curated by licensed surveyors at Versatile Geopick."
      />

      {/* Hero */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-stone-100 via-amber-50/30 to-stone-50 pt-24">
        <div className="absolute inset-0 z-0 opacity-25">
          <img
            src="https://images.unsplash.com/photo-1765475467677-579353b25ce0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA6MDV8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBjaXR5JTIwc2t5bGluZSUyMGFlcmlhbCUyMGFmcmljYSUyMGxhZ29zJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc5MDcyODU3MHww&ixlib=rb-4.1.0&q=85"
            alt="Lagos city skyline"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-stone-50/70 to-white"></div>
        </div>
        <Reveal className="relative z-10 text-center px-6 max-w-4xl mx-auto" duration={0.9}>
          <p className="text-emerald-700 font-medium tracking-wider uppercase text-sm mb-3 flex items-center justify-center gap-2">
            <Newspaper className="h-4 w-4" /> Versatile Property Hub
          </p>
          <h1 className="text-5xl md:text-7xl font-light text-stone-800 tracking-tight">
            Property News &<br /><span className="font-semibold text-emerald-700">Land Intelligence</span>
          </h1>
          <p className="text-xl text-stone-600 max-w-2xl mx-auto mt-6">
            Property News • Land Updates • Market Insights • Development Alerts
          </p>

          {/* Search */}
          <form onSubmit={applySearch} className="mt-10 flex max-w-xl mx-auto gap-3" data-testid="hub-search-form">
            <Input
              data-testid="hub-search-input"
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search Property & Land News..."
              className="flex-1 bg-white border-stone-300 rounded-full px-6 py-6 text-base shadow-sm"
            />
            <Button type="submit" data-testid="hub-search-button"
              className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-full px-8 py-6">
              <Search className="h-5 w-5" />
            </Button>
          </form>
          <div className="mt-4">
            <Input
              data-testid="hub-date-filter"
              type="date"
              value={filters.date}
              onChange={(e) => { setPage(1); setFilters((f) => ({ ...f, date: e.target.value })); }}
              className="max-w-[180px] mx-auto bg-white border-stone-300 rounded-full text-sm"
            />
          </div>
        </Reveal>
      </section>

      {/* Category chips */}
      <section className="py-8 border-b border-stone-200 bg-white sticky top-[64px] z-30 bg-white/95 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide" data-testid="category-filters">
            <button
              data-testid="category-all"
              onClick={() => setFilter('category', '')}
              className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                !filters.category ? 'bg-emerald-700 text-white' : 'bg-stone-100 text-stone-700 hover:bg-stone-200'
              }`}
            >
              All
            </button>
            {meta.categories.map((c) => (
              <button
                key={c}
                data-testid={`category-${c.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                onClick={() => setFilter('category', c)}
                className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  filters.category === c ? 'bg-emerald-700 text-white' : 'bg-stone-100 text-stone-700 hover:bg-stone-200'
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Location Watch */}
      <section className="py-10 bg-gradient-to-br from-stone-50 to-amber-50/20" data-testid="location-watch">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
              <MapPin className="h-5 w-5 text-emerald-700" />
            </div>
            <h2 className="text-2xl font-light text-stone-800">Location Watch</h2>
            {filters.location && (
              <button
                data-testid="clear-location-filter"
                onClick={() => setFilter('location', '')}
                className="text-sm text-emerald-700 underline"
              >
                Clear: {filters.location}
              </button>
            )}
          </Reveal>
          <RevealGroup className="flex flex-wrap gap-2" stagger={0.04}>
            {meta.locations.map((loc) => (
              <RevealItem key={loc} direction="scale">
                <button
                  data-testid={`location-${loc.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  onClick={() => setFilter('location', loc)}
                  className={`px-4 py-2 rounded-full text-sm transition-all ${
                    filters.location === loc
                      ? 'bg-emerald-700 text-white shadow-md'
                      : 'bg-white border border-stone-200 text-stone-700 hover:border-emerald-400'
                  }`}
                >
                  {loc}
                </button>
              </RevealItem>
            ))}
          </RevealGroup>
        </div>
      </section>

      {/* Articles grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          {loading ? (
            <div className="text-center py-20 text-stone-500" data-testid="articles-loading">Loading articles...</div>
          ) : articles.length === 0 ? (
            <div className="text-center py-20" data-testid="articles-empty">
              <p className="text-stone-600 text-lg">No articles match your filters yet.</p>
              <button
                data-testid="reset-filters"
                onClick={() => { setSearchInput(''); setFilters({ q: '', category: '', location: '', date: '' }); }}
                className="mt-4 text-emerald-700 font-medium underline"
              >
                Reset filters
              </button>
            </div>
          ) : (
            <>
              <RevealGroup className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" stagger={0.1}>
                {articles.map((a, i) => <ArticleCard key={a.id} article={a} index={i} />)}
              </RevealGroup>
              {pages > 1 && (
                <div className="flex justify-center gap-3 mt-14" data-testid="pagination">
                  {Array.from({ length: pages }, (_, i) => (
                    <button
                      key={i}
                      data-testid={`page-${i + 1}`}
                      onClick={() => setPage(i + 1)}
                      className={`w-10 h-10 rounded-full text-sm font-medium transition-colors ${
                        page === i + 1 ? 'bg-emerald-700 text-white' : 'bg-stone-100 text-stone-700 hover:bg-stone-200'
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Development Watch */}
      {developments.length > 0 && (
        <section className="py-20 bg-gradient-to-br from-stone-50 to-amber-50/20" data-testid="development-watch">
          <div className="max-w-7xl mx-auto px-6">
            <Reveal className="text-center mb-14">
              <p className="text-emerald-700 font-medium tracking-wider uppercase text-sm mb-3 flex items-center justify-center gap-2">
                <Building2 className="h-4 w-4" /> Development Watch
              </p>
              <h2 className="text-4xl md:text-5xl font-light text-stone-800 tracking-tight">
                Major Projects & Infrastructure
              </h2>
            </Reveal>
            <RevealGroup className="grid md:grid-cols-2 gap-8" stagger={0.12}>
              {developments.map((d) => (
                <RevealItem key={d.id}>
                  <div data-testid={`development-card-${d.id}`}
                    className="bg-white border border-stone-200 rounded-3xl p-8 h-full hover:border-emerald-300 hover:shadow-lg transition-all duration-300">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-2xl font-medium text-stone-800 mb-1">{d.name}</h3>
                        <p className="text-sm text-stone-500 flex items-center gap-1.5">
                          <MapPin className="h-4 w-4" /> {d.location}
                        </p>
                      </div>
                      <span className="px-3 py-1 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-full whitespace-nowrap">
                        {d.type}
                      </span>
                    </div>
                    <p className="text-stone-600 mb-4 leading-relaxed">{d.description}</p>
                    <div className="bg-stone-50 rounded-2xl p-4 mb-4">
                      <p className="text-xs font-medium text-stone-500 uppercase tracking-wide mb-1">Latest Update</p>
                      <p className="text-stone-700 text-sm">{d.latest_update}</p>
                      <p className="text-xs text-stone-500 mt-2">{formatDate(d.date_updated)}{d.source ? ` · Source: ${d.source}` : ''}</p>
                    </div>
                    {d.considerations && (
                      <div className="border-l-4 border-emerald-600 pl-4">
                        <p className="text-xs font-medium text-emerald-700 uppercase tracking-wide mb-1">Property Considerations</p>
                        <p className="text-sm text-stone-600">{d.considerations}</p>
                      </div>
                    )}
                  </div>
                </RevealItem>
              ))}
            </RevealGroup>
          </div>
        </section>
      )}

      {/* Subtle business CTA */}
      <section className="py-16 bg-white">
        <Reveal className="max-w-4xl mx-auto px-6" direction="scale">
          <div className="bg-emerald-50 border border-emerald-200 rounded-3xl p-10 text-center">
            <TrendingUp className="h-10 w-10 text-emerald-700 mx-auto mb-4" />
            <h3 className="text-2xl font-light text-stone-800 mb-3">Buying Land or Property?</h3>
            <p className="text-stone-600 mb-6 max-w-2xl mx-auto">
              Before purchasing, consider professional property verification, survey searches, boundary confirmation and title/documentation checks.
            </p>
            <Button className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-full px-8" asChild>
              <a href="https://wa.me/2348034494507" target="_blank" rel="noopener noreferrer" data-testid="hub-business-cta">
                Speak With Versatile Geopick
              </a>
            </Button>
          </div>
        </Reveal>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-emerald-700">
        <Reveal className="max-w-4xl mx-auto px-6 text-center" direction="scale">
          <h2 className="text-4xl md:text-5xl font-light text-white mb-6 leading-tight">
            Need Help With Land or Property?
          </h2>
          <p className="text-xl text-emerald-100 mb-10">
            Versatile Geopick provides professional surveying, property verification, land documentation and real-estate related services.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button size="lg" className="bg-white text-emerald-700 hover:bg-stone-50 px-10 py-6 text-lg" asChild>
              <Link to="/contact" data-testid="hub-contact-cta">
                <Phone className="mr-2 h-5 w-5" /> Contact Versatile Geopick
              </Link>
            </Button>
            <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-emerald-700 px-10 py-6 text-lg" asChild>
              <a href="https://versatilegeopick.com" target="_blank" rel="noopener noreferrer" data-testid="hub-website-cta">
                <Globe className="mr-2 h-5 w-5" /> Visit Versatilegeopick.com
              </a>
            </Button>
          </div>
        </Reveal>
      </section>
    </div>
  );
};

export default PropertyHub;
